import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import { cloneDeep, get, map, partial } from "lodash-es"
import moment from "moment"
import type { PreparedAction } from "../../../action-doc-builder"
import type { Narrative } from "../../create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"

interface Payload {
  cardStackId: string
  fromDate: string
  toDate: string
  fromCourse: Course
  toCourse: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export function prepareMoveLessonToDateOnDifferentCourse(payload: Payload): PreparedAction {
  return {
    name: MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE.name,
    payload: payload,
    patches: MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE.patches(payload),
    undoPatches: MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE.undoPatches(payload),
    narrative: partial(MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE.narrative, payload),
  }
}

const MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE = {
  name: "MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE",

  patches(payload: Payload) {
    // --------------------------------------------------
    // Remove lesson from old course
    // --------------------------------------------------
    let courseDatesWithoutLesson = map(
      payload.fromCourse.attributes.calendar.dates,
      (courseDate) => {
        if (courseDate.attributes.cardStackId === payload.cardStackId) {
          let cd = cloneDeep(courseDate)
          cd.attributes.cardStackId = null
          return cd
        } else {
          return courseDate
        }
      }
    )

    // --------------------------------------------------
    // Insert lesson
    // --------------------------------------------------
    let transferrableArray = [
      {
        cardStackId: payload.cardStackId,
        unitStart: [],
        unitEnd: [],
      },
    ]

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.toCourse,
      payload.planbook,
      payload.rotationCalendar
    )

    let newCourseDates = insertRange(
      transferrableArray,
      payload.toDate,
      0,
      null,
      payload.toCourse.attributes.calendar.dates,
      consolidatedCourseDatesOff,
      payload.rotationCalendar.attributes.schoolDays,
      true
    )

    let { courseDates, units } = constructUnitsAndDates(
      newCourseDates,
      consolidatedCourseDatesOff,
      payload.rotationCalendar.attributes.schoolDays
    )

    // --------------------------------------------------
    // Create patches
    // --------------------------------------------------
    // We might be creating a lesson here, so we need to set this
    let updateLessonPatch = Patch.create("card-stack", payload.cardStackId)
      .set("relationships.course.data.id", payload.toCourse.id)
      .set("relationships.planbook.data.id", payload.planbook.id)
      .set("attributes.schemaVersion", payload.planbook.attributes.flags.unifiedLessons ? 2 : 1)
      .set("attributes.usedAs", "lesson")
      .set("meta.permissions", payload.toCourse.meta.permissions).patch // to make sure we have the right permissions if this is new

    let removeFromOldCoursePatch = Patch.create("course", payload.fromCourse.id)
      .set("attributes.calendar.dates", courseDatesWithoutLesson)
      .inc("attributes.calendar.version", 1).patch

    let addToNewCoursePatch = Patch.create("course", payload.toCourse.id)
      .set("attributes.calendar.dates", courseDates)
      .set("attributes.calendar.units", units)
      .inc("attributes.calendar.version", 1).patch

    return [updateLessonPatch, removeFromOldCoursePatch, addToNewCoursePatch]
  },

  undoPatches(payload: Payload) {
    let updateLessonPatch = Patch.create("card-stack", payload.cardStackId).set(
      "relationships.course.data.id",
      payload.fromCourse.id
    ).patch

    let removeFromOldCoursePatch = Patch.create("course", payload.fromCourse.id)
      .set("attributes.calendar.dates", payload.fromCourse.attributes.calendar.dates)
      .inc("attributes.calendar.version", 1).patch

    let addToNewCoursePatch = Patch.create("course", payload.toCourse.id)
      .set("attributes.calendar.dates", payload.toCourse.attributes.calendar.dates)
      .set("attributes.calendar.units", payload.toCourse.attributes.calendar.units)
      .inc("attributes.calendar.version", 1).patch

    return [updateLessonPatch, removeFromOldCoursePatch, addToNewCoursePatch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let fromCourse = payload.fromCourse
    let toCourse = payload.toCourse
    let models = defaultLessonModels(payload, findFn, userId)

    let lessonTitle = get(models, "lesson.attributes.title")
    let fromCourseTitle = get(models, "fromCourse.attributes.title")
    let toCourseTitle = get(models, "toCourse.attributes.title")
    let formattedFromDate = moment(payload.fromDate).format("ddd, MMM. D, YYYY")
    let formattedToDate = moment(payload.toDate).format("ddd, MMM. D, YYYY")
    let newEvent: LessonEditMove = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.fromCourse.id],
      primary_verb: "move",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,

      adverbial_verb: "dragging_dropping",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "to_different_class",
      adverbial_object_id: payload.cardStackId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.toCourse.id],
    }
    return {
      title: "Moved Lesson",
      titleForAnalytics: "Lesson - Move to different class",
      description: `Moved "${lessonTitle}" from ${formattedFromDate} in "${fromCourseTitle}" to ${formattedToDate} in "${toCourseTitle}"`,
      context: {
        lessonTitle: models.cardStack?.attributes.title || "Blank Lesson",
        fromCourseTitle: payload.fromCourse.attributes.title,
        toCourseTitle: payload.toCourse.attributes.title,
        planbookTitle: payload.planbook.attributes.title,
        cardStackId: payload.cardStackId,
        fromCourseId: payload.fromCourse.id,
        toCourseId: payload.toCourse.id,
        planbookId: payload.planbook.id,
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${payload.fromCourse.id}`,
        to: [
          `course-edits:course-${get(models, "fromCourse.id")}`,
          `planbook-edits:planbook-${get(models, "planbook.id")}`,
        ],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE
